import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useGlobal } from "../contexts/GlobalContext";
import request from "../utils/request";
import { getErrorText } from "../utils/errorText";
import { getPageContext } from "../utils/pageContext";
import { streamJson } from "../utils/sse";
import ToolBindingPanel from "../components/agents/ToolBindingPanel";
import RunTraceTimeline from "../components/agents/RunTraceTimeline";
import { confirmDanger } from "../componets/common/confirmDanger";
import ModelSelect from "../componets/common/ModelSelect";
import StreamChatPanel from "../componets/common/StreamChatPanel";

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

interface AgentConfigPayload {
  basics?: { name?: string; description?: string; icon?: string; isPublic?: boolean };
  role?: { systemPrompt?: string; openingStatement?: string };
  model?: { model?: string; fallbackModels?: string[] };
  knowledge?: { knowledgeBaseIds?: number[] };
  tools?: { toolsConfig?: Record<string, any>[] };
  memory?: { maxHistoryMessages?: number };
  safety?: { enableContentFilter?: boolean; budgetCents?: number; maxRounds?: number };
  copilot?: { enabled?: boolean; mode?: string; siteActionPolicy?: string };
  publish?: { status: string; version: number; shareEnable: boolean };
  runtimeProfile?: string;
  system?: {
    isSystemDefault: boolean;
    systemKey?: string;
    lockedFields?: string[];
    restoreApi?: string;
  };
}

interface DebugEvent {
  kind: "tool.started" | "tool.finished" | "tool.failed" | "info";
  text: string;
}

// ── 子 Key ──────────────────────────────────────────────────────────────
const LANG_OPTIONS = [
  { value: "", label: "跟随用户 (follow)" },
  { value: "zh-CN", label: "简体中文" },
  { value: "zh-TW", label: "繁体中文" },
  { value: "en", label: "English" },
  { value: "ja", label: "日本語" },
  { value: "ko", label: "한국어" },
];

interface SubKey {
  id: number;
  name: string;
  key_prefix?: string;
  keyPrefix?: string;
  language?: string;
  enabled?: boolean;
  prompt?: string;
  agent_id?: number;
}

const langLabel = (lang?: string) =>
  LANG_OPTIONS.find((o) => o.value === (lang || ""))?.label || lang;

interface SubKeyPanelProps {
  agentId: number;
}

// Legacy API-key panel retained for migration compatibility; the product UI
// no longer renders it because role variants are prompt variants on one Agent.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SubKeyPanel: React.FC<SubKeyPanelProps> = ({ agentId }) => {
  const { API_BASE_URL } = useGlobal();
  const [keys, setKeys] = useState<SubKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<SubKey | null>(null);
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [form] = Form.useForm();

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/agents/${agentId}/sub-keys`);
      if (res.data.code === 200) setKeys(res.data.data || []);
      else message.error(res.data.msg || "加载子 Key 失败");
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "加载子 Key 失败");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, agentId]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ language: "", enabled: true });
    setEditOpen(true);
  };

  const openEdit = (row: SubKey) => {
    setEditing(row);
    form.setFieldsValue({ name: row.name, prompt: row.prompt || "", language: row.language || "" });
    setEditOpen(true);
  };

  const submit = async () => {
    const v = await form.validateFields();
    try {
      if (editing) {
        const res = await request.put(
          `${API_BASE_URL}/api/v3/agents/${agentId}/sub-keys/${editing.id}`,
          { name: v.name, prompt: v.prompt || "", language: v.language || "" }
        );
        if (res.data.code === 200) { message.success("已保存"); setEditOpen(false); loadKeys(); }
        else message.error(res.data.msg || "保存失败");
      } else {
        const res = await request.post(`${API_BASE_URL}/api/v3/agents/${agentId}/sub-keys`, {
          name: v.name, prompt: v.prompt || "", language: v.language || "",
        });
        if (res.data.code === 200) {
          setEditOpen(false);
          loadKeys();
          const plain = res.data.data?.api_key || res.data.data?.APIKey;
          if (plain) setPlaintext(plain);
          else message.success("已创建");
        } else message.error(res.data.msg || "创建失败");
      }
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "操作失败");
    }
  };

  const toggle = async (row: SubKey) => {
    try {
      await request.patch(`${API_BASE_URL}/api/v3/agents/${agentId}/sub-keys/${row.id}/toggle`);
      loadKeys();
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "操作失败");
    }
  };

  const remove = async (row: SubKey) => {
    try {
      await request.delete(`${API_BASE_URL}/api/v3/agents/${agentId}/sub-keys/${row.id}`);
      message.success("已删除"); loadKeys();
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "删除失败");
    }
  };

  const columns = [
    { title: "名称", dataIndex: "name", key: "name", render: (v: string) => <Text strong>{v}</Text> },
    {
      title: "Key 前缀", key: "prefix",
      render: (_: any, r: SubKey) => <Text code>{(r.key_prefix || r.keyPrefix || "—") + "…"}</Text>,
    },
    {
      title: "语言", key: "language",
      render: (_: any, r: SubKey) =>
        r.language ? <Tag color="purple">{langLabel(r.language)}</Tag> : <Tag>跟随用户</Tag>,
    },
    {
      title: "提示词覆盖", key: "prompt",
      render: (_: any, r: SubKey) =>
        r.prompt ? <Tag color="blue">已设置</Tag> : <Text type="secondary">沿用智能体</Text>,
    },
    {
      title: "状态", key: "enabled",
      render: (_: any, r: SubKey) => (
        <Switch checked={r.enabled !== false} size="small" onChange={() => toggle(r)} />
      ),
    },
    {
      title: "操作", key: "ops", width: 140,
      render: (_: any, r: SubKey) => (
        <Space size="small">
          <Button size="small" type="link" onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="删除该子 Key？删除后立即失效" onConfirm={() => remove(r)} okText="删除" cancelText="取消">
            <Button size="small" type="link" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="子 Key 跟随智能体，可独立设置系统提示词覆盖与语言策略。密钥明文仅在创建时展示一次。"
      />
      <div style={{ textAlign: "right", marginBottom: 8 }}>
        <Button type="primary" size="small" onClick={openCreate}>新建子 Key</Button>
      </div>
      <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={keys} pagination={false} locale={{ emptyText: "暂无子 Key" }} />

      <Modal title={editing ? "编辑子 Key" : "新建子 Key"} open={editOpen} onOk={submit} onCancel={() => setEditOpen(false)} okText={editing ? "保存" : "创建"} cancelText="取消">
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: "请输入名称" }]}>
            <Input placeholder="如：博客日文版" />
          </Form.Item>
          <Form.Item name="language" label="语言策略">
            <Select options={LANG_OPTIONS} />
          </Form.Item>
          <Form.Item name="prompt" label="智能体系统提示词覆盖（留空则沿用智能体配置）">
            <TextArea rows={4} placeholder="留空 = 沿用智能体提示词；填写后将追加/覆盖到智能体基础提示词之上" />
          </Form.Item>
          {!editing && (
            <Form.Item name="enabled" label="启用" valuePropName="checked" initialValue>
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal title="子 Key 已创建" open={!!plaintext} onCancel={() => setPlaintext(null)}
        footer={[
          <Button key="copy" type="primary" onClick={() => { if (plaintext) navigator.clipboard.writeText(plaintext).then(() => message.success("已复制")); }}>复制</Button>,
          <Button key="close" onClick={() => setPlaintext(null)}>我已保存</Button>,
        ]}>
        <Alert type="warning" showIcon message="请立即保存，密钥明文仅展示这一次，关闭后无法再次查看。" style={{ marginBottom: 12 }} />
        <Input.TextArea readOnly value={plaintext || ""} rows={2} />
      </Modal>
    </>
  );
};

// ── 主页面 ───────────────────────────────────────────────────────────────
const AgentConfig: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const agentId = Number(id);
  const navigate = useNavigate();
  const { API_BASE_URL, token } = useGlobal();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [config, setConfig] = useState<AgentConfigPayload | null>(null);
  const [models, setModels] = useState<{ label: string; value: string }[]>([]);
  const [projects, setProjects] = useState<{ label: string; value: number }[]>([]);
  const isSystemDefault = Boolean(config?.system?.isSystemDefault);

  const [debugQuery, setDebugQuery] = useState("");
  const [debugRunning, setDebugRunning] = useState(false);
  const [debugAnswer, setDebugAnswer] = useState("");
  const [debugReasoning, setDebugReasoning] = useState("");
  const [debugEvents, setDebugEvents] = useState<DebugEvent[]>([]);
  const [runsRefreshKey, setRunsRefreshKey] = useState(0);
  const debugAbortRef = useRef<AbortController | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/agents/${agentId}/config`);
      const cfg: AgentConfigPayload = res.data?.data;
      setConfig(cfg);
      form.setFieldsValue({
        name: cfg.basics?.name,
        description: cfg.basics?.description,
        isPublic: cfg.basics?.isPublic,
        systemPrompt: cfg.role?.systemPrompt,
        openingStatement: cfg.role?.openingStatement,
        model: cfg.model?.model || undefined,
        knowledgeBaseIds: cfg.knowledge?.knowledgeBaseIds || [],
        toolsConfig: cfg.tools?.toolsConfig || [],
        maxHistoryMessages: cfg.memory?.maxHistoryMessages,
        enableContentFilter: cfg.safety?.enableContentFilter,
        budgetCents: cfg.safety?.budgetCents,
        maxRounds: cfg.safety?.maxRounds ?? 0,
        copilotEnabled: cfg.copilot?.enabled ?? (cfg as any).safety?.copilotMode !== undefined,
        copilotMode: cfg.copilot?.mode ?? (cfg as any).safety?.copilotMode ?? "annotate",
        siteActionPolicy: cfg.copilot?.siteActionPolicy ?? (cfg as any).safety?.siteActionPolicy ?? "autonomous",
      });
    } catch (e) {
      message.error(getErrorText(e, "加载配置失败"));
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, agentId, form]);

  useEffect(() => {
    if (!agentId) return;
    loadConfig();
    request.get(`${API_BASE_URL}/api/v3/models`).then((res) => {
      const items = res.data?.data || [];
      setModels(items.map((m: any) => ({ label: m.public_model || m.publicModel || m.name, value: m.public_model || m.publicModel || m.name })));
    }).catch(() => {});
    request.get(`${API_BASE_URL}/api/v3/projects`).then((res) => {
      const items = res.data?.data?.items || res.data?.data || [];
      setProjects(items.map((p: any) => ({ label: p.name || p.project_name || `项目${p.id}`, value: p.id })));
    }).catch(() => {});
  }, [API_BASE_URL, agentId, loadConfig]);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload: AgentConfigPayload = {
        basics: { name: values.name, description: values.description, isPublic: values.isPublic },
        role: { systemPrompt: values.systemPrompt, openingStatement: values.openingStatement },
        model: { model: values.model },
        knowledge: { knowledgeBaseIds: values.knowledgeBaseIds },
        tools: { toolsConfig: values.toolsConfig },
        memory: { maxHistoryMessages: values.maxHistoryMessages },
        safety: { enableContentFilter: values.enableContentFilter, budgetCents: values.budgetCents, maxRounds: values.maxRounds },
        copilot: { enabled: values.copilotEnabled, mode: values.copilotMode, siteActionPolicy: values.siteActionPolicy },
      };
      const res = await request.put(`${API_BASE_URL}/api/v3/agents/${agentId}/config`, payload);
      if (res.data.code === 200) {
        setConfig(res.data.data);
        message.success("配置已保存(草稿,不影响线上,发布后生效)");
      } else {
        message.error(res.data.msg || "保存失败");
      }
    } catch (e) {
      message.error(getErrorText(e, "保存失败"));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await request.post(`${API_BASE_URL}/api/v3/agents/${agentId}/publish`);
      if (res.data.code === 200) { message.success("已发布新版本"); loadConfig(); }
      else message.error(res.data.msg || "发布失败");
    } catch (e) {
      message.error(getErrorText(e, "发布失败"));
    } finally {
      setPublishing(false);
    }
  };

  const handleRestoreDefault = () => {
    if (!config?.system?.restoreApi) return;
    confirmDanger({
      title: "恢复默认知识库 Agent?",
      consequence: "将恢复默认知识库检索、运行档位、发布快照和安全能力配置；不会覆盖历史会话、统计、API Key 或知识库内容。",
      okText: "确认恢复",
      onOk: async () => {
        try {
          const res = await request.post(`${API_BASE_URL}${config.system?.restoreApi}`);
          if (res.data.code === 200) { message.success("默认 Agent 已恢复"); await loadConfig(); }
          else message.error(res.data.msg || "恢复失败");
        } catch (e) { message.error(getErrorText(e, "恢复失败")); }
      },
    });
  };

  const runDebug = async () => {
    const query = debugQuery.trim();
    if (!query || debugRunning) return;
    setDebugRunning(true);
    setDebugAnswer(""); setDebugReasoning(""); setDebugEvents([]);
    let accumulated = ""; let reasoning = "";
    try {
      debugAbortRef.current?.abort();
      debugAbortRef.current = new AbortController();
      await streamJson<any>(`${API_BASE_URL}/api/v3/agents/${agentId}/run`, {
        method: "POST", token,
        body: JSON.stringify({ query, input: {}, conversationId: 0, stream: true, pageContext: getPageContext() }),
        signal: debugAbortRef.current.signal,
        onJson: (data, eventName) => {
          switch (eventName) {
            case "run.started":
              setDebugEvents((prev) => [...prev, { kind: "info", text: `运行开始 #${data.runId?.slice(0, 8)}` }]); break;
            case "tool.started":
              setDebugEvents((prev) => [...prev, { kind: "tool.started", text: `工具 ${data.toolKey} 开始` }]); break;
            case "tool.finished":
              setDebugEvents((prev) => [...prev, { kind: "tool.finished", text: `工具 ${data.toolKey} 完成 (${data.durationMs}ms)` }]); break;
            case "tool.failed":
              setDebugEvents((prev) => [...prev, { kind: "tool.failed", text: `工具 ${data.toolKey} 失败:${data.userMessage}` }]); break;
            case "run.finished":
              setDebugEvents((prev) => [...prev, { kind: "info", text: `运行完成 (${data.durationMs}ms${data.model ? ", " + data.model : ""})` }]); break;
            default:
              if (data.reasoningContent) { reasoning += data.reasoningContent; setDebugReasoning(reasoning); }
              if (data.content) { accumulated += data.content; setDebugAnswer(accumulated); }
          }
        },
        onerror: (err) => { throw err; },
      });
    } catch (e) {
      message.error(getErrorText(e, "调试运行失败"));
    } finally {
      setDebugRunning(false);
      setRunsRefreshKey((k) => k + 1);
    }
  };

  if (!agentId) return <Card>无效的 Agent ID</Card>;

  return (
    <Spin spinning={loading}>
      <Card
        title={
          <Space>
            <span>Agent 配置</span>
            {isSystemDefault && <Tag color="gold">默认知识库</Tag>}
            {config?.runtimeProfile && <Tag color="blue">{config.runtimeProfile}</Tag>}
            {config?.publish && (
              <Tag color={config.publish.status === "active" ? "green" : "default"}>
                {config.publish.status === "active" ? `已发布 v${config.publish.version}` : "草稿"}
              </Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <Button onClick={() => navigate("/agents")}>返回列表</Button>
            {false && (
              <Button onClick={() => confirmDanger({
                title: "升级到高级编排?",
                consequence: "将根据当前角色/模型/知识配置生成等价编排图,并把 Agent 切换为工作流类型。此操作单向不可逆,之后只能在画布中维护。",
                okText: "确认升级",
                onOk: async () => {
                  try {
                    const res = await request.post(`${API_BASE_URL}/api/v3/agents/${agentId}/upgrade-to-workflow`);
                    if (res.data.code === 200) { message.success("已升级,正在进入画布"); navigate(`/agents/${agentId}/studio`); }
                    else message.error(res.data.msg || "升级失败");
                  } catch (e) { message.error(getErrorText(e, "升级失败")); }
                },
              })}>
                升级到高级编排
              </Button>
            )}
            {isSystemDefault && <Button onClick={handleRestoreDefault}>恢复默认</Button>}
            <Button onClick={handleSave} loading={saving}>保存草稿</Button>
            <Button type="primary" onClick={handlePublish} loading={publishing}>发布</Button>
          </Space>
        }
      >
        {isSystemDefault && (
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="这是项目默认知识库 Agent"
            description="该 Agent 承接老知识库问答和搜索链路，不可删除，也不能解绑当前项目知识库；其他安全字段仍可编辑，必要时可一键恢复默认。"
          />
        )}
        <Row gutter={16}>
          <Col xs={24} lg={14}>
            <Form form={form} layout="vertical">
              <Collapse
                defaultActiveKey={["basics", "role", "model"]}
                items={[
                  {
                    key: "basics",
                    label: "基础",
                    children: (
                      <>
                        <Form.Item name="name" label="名称" rules={[{ required: true, message: "请输入名称" }]}>
                          <Input maxLength={50} />
                        </Form.Item>
                        <Form.Item name="description" label="描述">
                          <TextArea rows={2} maxLength={500} />
                        </Form.Item>
                        <Form.Item name="isPublic" label="公开到智能体市场" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </>
                    ),
                  },
                  {
                    key: "role",
                    label: "角色与提示词",
                    children: (
                      <>
                        <Form.Item name="systemPrompt" label="系统提示词" extra="定义 Agent 的角色、能力边界与回答风格">
                          <TextArea rows={6} />
                        </Form.Item>
                        <Form.Item name="openingStatement" label="开场白">
                          <TextArea rows={2} />
                        </Form.Item>
                      </>
                    ),
                  },
                  {
                    key: "model",
                    label: "模型",
                    children: (
                      <Form.Item name="model" label="对话模型" extra="留空时使用系统默认模型">
                        <ModelSelect models={models} placeholder="系统默认" />
                      </Form.Item>
                    ),
                  },
                  {
                    key: "knowledge",
                    label: "知识",
                    children: (
                      <Form.Item name="knowledgeBaseIds" label="绑定知识库" extra="绑定后回答时自动检索;Agent 可调用 knowledge_search 工具主动查询">
                        <Select mode="multiple" options={projects} placeholder="选择项目知识库" disabled={isSystemDefault} />
                      </Form.Item>
                    ),
                  },
                  {
                    key: "tools",
                    label: "工具",
                    children: (
                      <Form.Item name="toolsConfig" noStyle>
                        <ToolBindingPanel knowledgeOptions={projects} />
                      </Form.Item>
                    ),
                  },
                  {
                    key: "memory",
                    label: "记忆",
                    children: (
                      <Form.Item name="maxHistoryMessages" label="历史消息取数上限" extra="0 = 按 token 预算自动裁剪(推荐);显式设置则作为取数上限">
                        <InputNumber min={0} max={200} style={{ width: 160 }} />
                      </Form.Item>
                    ),
                  },
                  {
                    key: "copilot",
                    label: "Copilot",
                    children: (
                      <>
                        <Alert
                          type="info" showIcon style={{ marginBottom: 12 }}
                          message="Copilot 让访客用自然语言操作网页：搜索、跳转、填写表单。开启后该智能体将具备页面读取和操作能力。"
                        />
                        <Form.Item name="copilotEnabled" label="开启 Copilot" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                        <Form.Item name="copilotMode" label="Copilot 模式" extra="标注：仅操作站长预先标注的元素；全自动：AI 自动扫描页面 DOM，自行判断读取/点击/填写">
                          <Select style={{ maxWidth: 320 }} options={[
                            { label: "标注模式（全站标注元素）", value: "annotate" },
                            { label: "全自动模式（AI 自动处理 DOM）", value: "auto" },
                          ]} />
                        </Form.Item>
                        <Form.Item name="siteActionPolicy" label="站点动作策略">
                          <Select style={{ maxWidth: 320 }} options={[
                            { label: "允许自动执行", value: "autonomous" },
                            { label: "始终需要确认", value: "confirm" },
                            { label: "关闭站点动作", value: "off" },
                          ]} />
                        </Form.Item>
                      </>
                    ),
                  },
                  {
                    key: "safety",
                    label: "安全与成本",
                    children: (
                      <>
                        <Form.Item name="enableContentFilter" label="内容安全过滤" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                        <Form.Item name="budgetCents" label="周期预算(分)" extra="0 = 不限制;超出后拒绝运行">
                          <InputNumber min={0} style={{ width: 200 }} />
                        </Form.Item>
                        <Form.Item name="maxRounds" label="对话最大轮次" extra="agentic 工具循环的最大轮数。0 = 用内置默认;最大 50。">
                          <InputNumber min={0} max={50} style={{ width: 200 }} />
                        </Form.Item>
                      </>
                    ),
                  },
                  {
                    key: "prompt-variants",
                    label: "提示词变体",
                    children: (
                      <Alert
                        type="info"
                        showIcon
                        message="提示词变体"
                        description="一个项目只使用一个主 Agent。不同角色请通过系统提示词维护变体，不创建子 Agent；OpenAI 兼容接口始终只走纯对话能力，不会获得 Copilot 页面操作权限。"
                      />
                    ),
                  },
                ]}
              />
            </Form>
          </Col>

          <Col xs={24} lg={10}>
            <Card size="small" title="调试">
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  value={debugQuery}
                  onChange={(e) => setDebugQuery(e.target.value)}
                  onPressEnter={runDebug}
                  placeholder="输入问题,按当前已保存配置运行"
                  disabled={debugRunning}
                />
                <Button type="primary" onClick={runDebug} loading={debugRunning}>运行</Button>
              </Space.Compact>
              <div style={{ marginTop: 12 }}>
                {debugReasoning && (
                  <Collapse size="small" style={{ marginBottom: 10 }}
                    items={[{ key: "reasoning", label: "Reasoning", children: <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>{debugReasoning}</Paragraph> }]}
                  />
                )}
                <StreamChatPanel
                  answer={debugAnswer} running={debugRunning} events={debugEvents}
                  emptyText="回复与工具时间线会显示在这里。注意:调试使用已保存的草稿配置。"
                />
              </div>
            </Card>
            <Card size="small" title="运行记录(点击查看 Trace)" style={{ marginTop: 16 }}>
              <RunTraceTimeline agentId={agentId} refreshKey={runsRefreshKey} />
            </Card>
          </Col>
        </Row>
      </Card>
    </Spin>
  );
};

export default AgentConfig;
